import { useEffect, useState } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, InputNumber, Select, DatePicker, Tag, Empty, Spin, Space, Typography, Row, Col, Drawer, Statistic, message, Popconfirm } from 'antd'
import { PlusOutlined, DeleteOutlined, CalendarOutlined, UserOutlined, ClockCircleOutlined, FieldTimeOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography

export default function Riegos({ usuario }) {
  const isMobile = useIsMobile()
  const [riegos, setRiegos] = useState([])
  const [cultivos, setCultivos] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [cultivoSel, setCultivoSel] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [r, c, p, u] = await Promise.all([
        api('/riegos'), api('/cultivos'), api('/parcelas'), api('/usuarios')
      ])
      setRiegos(r)
      setCultivos(c)
      setParcelas(p)
      setUsuarios(u)
    } catch (err) {
      console.error('Error cargando riegos:', err)
    }
    setCargando(false)
  }

  const abrirNuevo = () => {
    form.resetFields()
    form.setFieldsValue({ fecha: dayjs() })
    setCultivoSel(null)
    setMostrarForm(true)
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      await api('/riegos', {
        method: 'POST',
        body: JSON.stringify({
          fecha: values.fecha.format('YYYY-MM-DD'),
          cultivo_id: values.cultivo_id,
          parcelas: values.parcelas || [],
          trabajador_id: values.trabajador_id || null,
          descripcion: values.descripcion || '',
          dias_proximo: values.dias_proximo || null,
          horas: values.horas || null,
          precio_hora: values.precio_hora || null,
        }),
      })
      message.success('Riego registrado')
      setMostrarForm(false)
      form.resetFields()
      setCultivoSel(null)
      cargar()
    } catch (err) {
      message.error('Error registrando riego')
    }
    setGuardando(false)
  }

  const eliminar = async (id) => {
    try {
      await api(`/riegos/${id}`, { method: 'DELETE' })
      message.success('Riego eliminado')
      cargar()
    } catch (err) {
      message.error('Error eliminando riego')
    }
  }

  const onCultivoChange = (cultivoId) => {
    setCultivoSel(cultivoId)
    form.setFieldsValue({ parcelas: [] })
  }

  // Parcelas filtradas por cultivo seleccionado
  const cultivoObj = cultivos.find(c => c.id === cultivoSel)
  const parcelasDisponibles = cultivoObj && cultivoObj.parcelas && cultivoObj.parcelas.length > 0
    ? parcelas.filter(p => cultivoObj.parcelas.some(cp => cp.id === p.id))
    : parcelas

  // Stats
  const mesActual = dayjs().format('YYYY-MM')
  const riegosMes = riegos.filter(r => r.fecha && r.fecha.startsWith(mesActual))
  const proximoRiego = riegos
    .filter(r => r.fecha_proximo_riego && dayjs(r.fecha_proximo_riego).isAfter(dayjs().subtract(1, 'day')))
    .sort((a, b) => dayjs(a.fecha_proximo_riego).diff(dayjs(b.fecha_proximo_riego)))[0]

  const esAdmin = usuario?.rol === 'admin'
  const soloLectura = usuario?.rol === 'tecnico' || usuario?.rol === 'usuario'

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Riegos</Title>
        {!soloLectura && (
          <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>
            {!isMobile && 'Nuevo'}
          </Button>
        )}
      </div>

      <Drawer
        title="Nuevo riego"
        open={mostrarForm}
        onClose={() => { setMostrarForm(false); setCultivoSel(null) }}
        width={isMobile ? '100%' : 400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setMostrarForm(false); setCultivoSel(null) }}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>Registrar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true, message: 'Obligatorio' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item label="Cultivo" name="cultivo_id" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Select placeholder="Selecciona cultivo" onChange={onCultivoChange} showSearch optionFilterProp="children">
              {cultivos.filter(c => c.estado === 'activo').map(c => (
                <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Parcelas" name="parcelas" rules={[{ required: true, message: 'Selecciona al menos una parcela' }]}>
            <Select mode="multiple" placeholder="Selecciona parcelas" showSearch optionFilterProp="children">
              {parcelasDisponibles.map(p => (
                <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Realizado por" name="trabajador_id">
            <Select placeholder="Selecciona trabajador" allowClear showSearch optionFilterProp="children">
              {usuarios.filter(u => u.activo !== false).map(u => (
                <Select.Option key={u.id} value={u.id}>{u.nombre} {u.rol ? `(${u.rol})` : ''}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Días hasta próximo riego" name="dias_proximo">
            <InputNumber min={1} step={1} style={{ width: '100%' }} placeholder="Ej: 15" />
          </Form.Item>
          <Form.Item label="Descripción" name="descripcion">
            <Input placeholder="Notas sobre el riego..." />
          </Form.Item>
          {esAdmin && (
            <>
              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '13px', color: '#389e0d', display: 'block', marginBottom: '8px' }}>
                  <ClockCircleOutlined /> Jornales y coste
                </Text>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="Horas" name="horas" style={{ marginBottom: 0 }}>
                      <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} placeholder="Ej: 4" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="€/hora" name="precio_hora" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Ej: 12" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {/* Stats */}
        <Row gutter={8} style={{ marginBottom: '12px' }}>
          <Col span={12}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic
                title={<Text style={{ fontSize: '11px' }}>Riegos este mes</Text>}
                value={riegosMes.length}
                valueStyle={{ fontSize: '22px', color: '#0288D1' }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              {proximoRiego ? (
                <div>
                  <Text style={{ fontSize: '11px', color: '#999' }}>Próximo riego</Text>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#E65100' }}>
                    {dayjs(proximoRiego.fecha_proximo_riego).format('DD/MM')}
                  </div>
                  <Text style={{ fontSize: '11px', color: '#666' }}>
                    {dayjs(proximoRiego.fecha_proximo_riego).diff(dayjs(), 'day')} días - {proximoRiego.cultivo_nombre}
                  </Text>
                </div>
              ) : (
                <Statistic
                  title={<Text style={{ fontSize: '11px' }}>Próximo riego</Text>}
                  value="—"
                  valueStyle={{ fontSize: '22px', color: '#999' }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : riegos.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay riegos registrados</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Pulsa "Nuevo" para registrar uno</Text>
              </Space>
            }
            style={{ marginTop: '40px' }}
          />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {riegos.map(r => {
              const diasHasta = r.fecha_proximo_riego ? dayjs(r.fecha_proximo_riego).diff(dayjs(), 'day') : null
              let colorProximo = 'default'
              if (diasHasta !== null) {
                if (diasHasta <= 0) colorProximo = 'red'
                else if (diasHasta <= 3) colorProximo = 'orange'
                else if (diasHasta <= 7) colorProximo = 'gold'
                else colorProximo = 'green'
              }
              const parcelasList = typeof r.parcelas === 'string' ? JSON.parse(r.parcelas) : r.parcelas

              return (
                <Card key={r.id} size="small" hoverable style={{ borderRadius: '12px', borderLeft: '3px solid #0288D1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: '12px' }}><CalendarOutlined /> {dayjs(r.fecha).format('DD/MM/YYYY')}</Text>
                        {r.cultivo_nombre && <Tag color="green" style={{ fontSize: '11px' }}>{r.cultivo_nombre}</Tag>}
                      </div>
                      <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {parcelasList && parcelasList.map(p => (
                          <Tag key={p.id} style={{ fontSize: '10px' }}>{p.nombre}</Tag>
                        ))}
                      </div>
                      {r.trabajador_nombre && (
                        <Text style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}>
                          <UserOutlined /> {r.trabajador_nombre}
                        </Text>
                      )}
                      {r.descripcion && <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px' }}>{r.descripcion}</Text>}
                      {r.fecha_proximo_riego && (
                        <Tag color={colorProximo} style={{ fontSize: '11px', marginTop: '4px' }}>
                          <FieldTimeOutlined /> Próximo: {dayjs(r.fecha_proximo_riego).format('DD/MM/YYYY')}
                          {diasHasta !== null && ` (${diasHasta <= 0 ? 'HOY o pasado' : diasHasta + ' días'})`}
                        </Tag>
                      )}
                    </div>
                    {esAdmin && (
                      <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(r.id)} okText="Sí" cancelText="No">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </div>
                </Card>
              )
            })}
          </Space>
        )}
      </div>
    </div>
  )
}
